import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { api, type Module, type MyWork, type WorkItem } from "../lib/api";

/**
 * "Your work now", fetched once per session and shared.
 *
 * WHY A CONTEXT AND NOT A HOOK IN HOME
 *
 * Two places need the same numbers: Home draws them as rows, and both navs
 * draw them as badges. The nav is rendered by AppShell, which sits ABOVE
 * every screen, so a hook inside Home could not reach it - and two separate
 * hooks would mean two requests and, worse, two answers, with the badge and
 * the row disagreeing after one of them refetched.
 *
 * So the provider goes above the shell and both read the same snapshot.
 *
 * WHY IT DOES NOT POLL
 *
 * A dashboard that refreshes itself is a dashboard that changes under
 * somebody's thumb. These counts move when this person acts, so the provider
 * refetches on `reload()` - which Home's own actions call, and which the
 * screens that complete a step call when they navigate back - and otherwise
 * when the tab is brought back to the foreground after being away. Field
 * staff live on metered connections; a five-second timer is not free.
 *
 * A FAILURE HERE IS NOT AN ERROR SCREEN
 *
 * Every number is a shortcut to a list that is reachable from the nav
 * anyway, so if this request fails the app is not broken - the shortcuts are
 * simply missing. Home says so quietly and offers a retry; the nav drops its
 * badges. Nothing here ever blocks a screen from rendering.
 */

interface MyWorkValue {
  items: WorkItem[];
  missingDeliveries: number;
  missingPhones: number;
  status: "loading" | "ready" | "error";
  /** The total across every row - the pill in the card header. */
  total: number;
  /** The badge for one module: how much of its work is waiting. */
  countFor: (module: Module) => number;
  /**
   * How that badge should read. "alert" if ANY of the module's items is one -
   * a module with both a queue and an empty shelf is a module with an empty
   * shelf, and the louder of the two is the one worth drawing.
   */
  toneFor: (module: Module) => "neutral" | "waiting" | "alert";
  reload: () => void;
}

const EMPTY: MyWorkValue = {
  items: [],
  missingDeliveries: 0,
  missingPhones: 0,
  status: "loading",
  total: 0,
  countFor: () => 0,
  toneFor: () => "neutral",
  reload: () => {},
};

const MyWorkContext = createContext<MyWorkValue>(EMPTY);

export function MyWorkProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth();
  const signedIn = authStatus === "signedIn";
  const [work, setWork] = useState<MyWork | null>(null);
  const [status, setStatus] = useState<MyWorkValue["status"]>("loading");
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!signedIn) {
      // Not merely "no data": the previous person's counts must not survive a
      // sign-out, and a stale snapshot is exactly what a shared handset would
      // show the next user.
      setWork(null);
      setStatus("loading");
      return;
    }
    let live = true;
    setStatus((current) => (current === "ready" ? "ready" : "loading"));
    api
      .myWork()
      .then((result) => {
        if (!live) return;
        setWork(result);
        setStatus("ready");
      })
      .catch(() => {
        if (!live) return;
        setStatus("error");
      });
    return () => {
      live = false;
    };
  }, [signedIn, nonce]);

  // Coming back to a tab that has been in the background is the one moment
  // the numbers are reliably stale through no action of this person's -
  // somebody else has been working. Cheap, and never while they are looking.
  useEffect(() => {
    if (!signedIn) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") reload();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [signedIn, reload]);

  const value = useMemo<MyWorkValue>(() => {
    const items = work?.items ?? [];
    const perModule = new Map<string, number>();
    const alerting = new Set<string>();
    for (const item of items) {
      perModule.set(item.module, (perModule.get(item.module) ?? 0) + item.count);
      if (item.tone === "alert") alerting.add(item.module);
    }
    return {
      items,
      missingDeliveries: work?.missingDeliveries ?? 0,
      missingPhones: work?.missingPhones ?? 0,
      status,
      total: items.reduce((sum, item) => sum + item.count, 0),
      countFor: (module: Module) => perModule.get(module) ?? 0,
      toneFor: (module: Module) =>
        alerting.has(module)
          ? "alert"
          : (perModule.get(module) ?? 0) > 0
            ? "waiting"
            : "neutral",
      reload,
    };
  }, [work, status, reload]);

  return <MyWorkContext.Provider value={value}>{children}</MyWorkContext.Provider>;
}

export function useMyWork(): MyWorkValue {
  return useContext(MyWorkContext);
}
